// Procedural jewelry-store mannequin bust (upper torso + neck only) and the static chain drape over it.
// Everything above "THREE geometry" is pure (no DOM, no Lume) so it is node-testable.
//
// Coordinates are scene coordinates: x right, y DOWN, z toward the viewer. "Bust-local" means y = 0 at
// the necklace level on the neck (the chain's neck loop) and z = 0 on the neck axis; the bust is placed in
// the world by a (yn, zShift) offset. No physics: the chain is laid along the sampled contour.
import {BufferGeometry, Float32BufferAttribute} from 'three'
import {pathLength} from './chain.js'

/**
 * Scene units per inch of chain when the bust is shown. A real chain wraps a ~14in neck, so the neck loop here
 * (circumference ~630 units) is ~14in: 12in is a snug choker, 16-22in drop to a V on the chest.
 */
export const BUST_UPI = 44

/** The knots below are authored at roughly 1/1.3 of the final size; this scales the bust up so a V fits under a pendant. */
const SCALE = 1.3

/** Angle (rad from straight ahead) where the chain leaves the neck's side and goes up and round the back. */
const NECK_ANGLE = 68 * (Math.PI / 180)
/** Gap between skin and chain centreline. */
const SKIN_GAP = 3.4

/** @typedef {[number, number, number]} V3 */

/**
 * Cross-section knots, bust-local y (down; 0 = base of the neck where the chain wraps) -> half-width a,
 * half-depth b, centre z, superellipse exponent n. Slim neck (about 1/3 of the shoulder width at its base),
 * concave trapezius sloping out to rounded shoulder caps, soft shallow chest, cut off at the upper arms.
 * @type {{y: number, a: number, b: number, zc: number, n: number}[]}
 */
const RAW_KNOTS = [
  {y: -430, a: 50, b: 48, zc: 10, n: 2},
  {y: -300, a: 52, b: 50, zc: 9, n: 2},
  {y: -180, a: 57, b: 54, zc: 6, n: 2},
  {y: -80, a: 68, b: 60, zc: 3, n: 2.05},
  {y: 0, a: 84, b: 64, zc: 2, n: 2.1},
  {y: 25, a: 115, b: 70, zc: 4, n: 2.2},
  {y: 50, a: 165, b: 78, zc: 8, n: 2.3},
  {y: 75, a: 220, b: 88, zc: 13, n: 2.5},
  {y: 100, a: 262, b: 98, zc: 18, n: 2.7},
  {y: 130, a: 288, b: 108, zc: 26, n: 2.8},
  {y: 180, a: 300, b: 118, zc: 36, n: 2.8},
  {y: 280, a: 294, b: 128, zc: 47, n: 2.7},
  {y: 420, a: 282, b: 136, zc: 54, n: 2.6},
]

/** Vertical extent of the bust (bust-local y). */
/** @type {typeof RAW_KNOTS} */
const KNOTS = RAW_KNOTS.map(k => ({...k, y: k.y * SCALE, a: k.a * SCALE, b: k.b * SCALE, zc: k.zc * SCALE}))

export const BUST_TOP = KNOTS[0].y
export const BUST_BOTTOM = KNOTS[KNOTS.length - 1].y

/**
 * Smooth (cubic Hermite, finite-difference tangents) interpolation of one knot field at y.
 * @param {'a' | 'b' | 'zc' | 'n'} key
 * @param {number} y bust-local y
 * @returns {number}
 */
function interp(key, y) {
  const k = KNOTS
  if (y <= k[0].y) return k[0][key]
  if (y >= k[k.length - 1].y) return k[k.length - 1][key]
  let i = 0
  while (y > k[i + 1].y) i++
  const p0 = k[Math.max(0, i - 1)]
  const p1 = k[i]
  const p2 = k[i + 1]
  const p3 = k[Math.min(k.length - 1, i + 2)]
  const h = p2.y - p1.y
  const t = (y - p1.y) / h
  const m1 = ((p2[key] - p0[key]) / (p2.y - p0.y)) * h
  const m2 = ((p3[key] - p1[key]) / (p3.y - p1.y)) * h
  const t2 = t * t
  const t3 = t2 * t
  return (2 * t3 - 3 * t2 + 1) * p1[key] + (t3 - 2 * t2 + t) * m1 + (-2 * t3 + 3 * t2) * p2[key] + (t3 - t2) * m2
}

/**
 * Cross-section parameters at bust-local y.
 * @param {number} y
 * @returns {{a: number, b: number, zc: number, n: number}}
 */
export function bustRing(y) {
  return {a: interp('a', y), b: interp('b', y), zc: interp('zc', y), n: interp('n', y)}
}

/** @param {number} u @returns {number} */
const gauss = u => Math.exp(-u * u)
/** @param {number} e0 @param {number} e1 @param {number} x @returns {number} */
const smooth = (e0, e1, x) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)))
  return t * t * (3 - 2 * t)
}

/**
 * Forward (+z) displacement of the front skin: sternal notch hollow, collarbone ridges with soft hollows above and
 * below them. Bust-local x, y.
 * @param {number} x
 * @param {number} y
 * @returns {number}
 */
export function bustDetail(x, y) {
  const S = SCALE
  const ax = Math.abs(x)
  const notch = -12 * S * gauss(x / (20 * S)) * gauss((y - 8 * S) / (14 * S))
  // clavicle: from the notch, out and slightly up toward the shoulder tip
  const yc = 26 * S - 0.075 * ax
  const fade = smooth(14 * S, 55 * S, ax) * (1 - smooth(150 * S, 215 * S, ax))
  const ridge = 7 * S * gauss((y - yc) / (8 * S)) * fade
  const above = -4.5 * S * gauss((y - (yc - 24 * S)) / (16 * S)) * fade
  const below = -4 * S * gauss((y - (yc + 26 * S)) / (20 * S)) * fade
  return notch + ridge + above + below
}

/**
 * Z of the front surface at (x, y) (bust-local); |x| beyond the section clamps to the section's edge.
 * @param {number} x
 * @param {number} y
 * @returns {number}
 */
export function bustFrontZ(x, y) {
  const {a, b, zc, n} = bustRing(y)
  const u = Math.min(1, Math.abs(x) / a)
  return zc + b * Math.pow(1 - Math.pow(u, n), 1 / n) + bustDetail(x, y)
}

/**
 * Slope dz/dy of the front surface on the centre line (positive: the chest bulges forward going down).
 * @param {number} y bust-local y
 * @returns {number}
 */
export function bustSlope(y) {
  return (bustFrontZ(0, y + 2) - bustFrontZ(0, y - 2)) / 4
}

/**
 * PURE: chain centreline over the mannequin. Start at the two pendant thread points, climb the chest in a
 * V to the neck's sides, then wrap round the back of the neck. The V depth is solved so the smoothed path
 * is exactly `len` long (shorter chain = shallower V, higher pendant). Static: no physics.
 * @param {V3} left left thread point (world scene coords)
 * @param {V3} right right thread point
 * @param {number} len chain length in scene units
 * @param {number} zShift bust-local z -> world z
 * @returns {{points: V3[], neckY: number, depth: number}} world control points, world y of the neck loop, V depth
 */
export function drapePath(left, right, len, zShift) {
  const ry = Math.min(left[1], right[1])
  /** @param {number} yn @returns {V3[]} */
  const build = yn => {
    const nr = bustRing(0)
    const ax = nr.a + SKIN_GAP
    const az = nr.b + SKIN_GAP
    const sx = ax * Math.sin(NECK_ANGLE)
    /** @param {V3} t @param {number} sgn -1 left, +1 right @returns {V3[]} */
    const side = (t, sgn) => {
      /** @type {V3[]} */
      const pts = [t, [t[0], t[1] - 3, t[2]]]
      const y0 = t[1] - 3
      const x0 = t[0]
      const xn = sgn * sx
      const N = 9
      for (let k = 1; k <= N; k++) {
        const s = k / N
        const y = y0 + (yn - y0) * s
        // a straight-ish V with a slight gravity sag (a touch more width low down)
        const x = x0 + (xn - x0) * (s * 0.9 + s * s * (3 - 2 * s) * 0.1 - 0.05 * Math.sin(Math.PI * s))
        const zs = bustFrontZ(x, y - yn) + zShift + SKIN_GAP
        const w = Math.min(1, s * 3)
        pts.push([x, y, t[2] * (1 - w) + zs * w])
      }
      return pts
    }
    const l = side(left, -1)
    const r = side(right, 1)
    /** @type {V3[]} */
    const back = []
    const M = 16
    for (let k = 1; k < M; k++) {
      const th = -NECK_ANGLE - (k / M) * (2 * Math.PI - 2 * NECK_ANGLE)
      back.push([ax * Math.sin(th), yn, nr.zc + az * Math.cos(th) + zShift])
    }
    // list runs from the left thread point up the left side, round the back, down the right side to the right thread point
    return [...l, ...back, ...r.reverse()]
  }
  let lo = 0
  let hi = len
  const lenAt = (/** @type {number} */ d) => pathLength(build(ry - d))
  if (lenAt(2) >= len) return {points: build(ry - 2), neckY: ry - 2, depth: 2}
  for (let i = 0; i < 34; i++) {
    const mid = (lo + hi) / 2
    if (lenAt(mid) < len) lo = mid
    else hi = mid
  }
  const depth = (lo + hi) / 2
  return {points: build(ry - depth), neckY: ry - depth, depth}
}

// ---------------------------------------------------------------- THREE geometry

/**
 * Lofted mannequin surface (y-UP three space: vertex y = -bust-local y). Neck top and torso bottom are capped.
 * @param {number} [rings] rings along y
 * @param {number} [seg] segments around
 * @returns {BufferGeometry}
 */
export function bustGeometry(rings = 220, seg = 168) {
  /** @type {number[]} */
  const pos = []
  /** @type {number[]} */
  const idx = []
  for (let r = 0; r <= rings; r++) {
    const y = BUST_TOP + ((BUST_BOTTOM - BUST_TOP) * r) / rings
    const {a, b, zc, n} = bustRing(y)
    for (let s = 0; s < seg; s++) {
      const th = (s / seg) * Math.PI * 2 // 0 = front, increasing toward +x
      const sn = Math.sin(th)
      const cs = Math.cos(th)
      const k = Math.pow(Math.pow(Math.abs(sn) / a, n) + Math.pow(Math.abs(cs) / b, n), -1 / n)
      const x = k * sn
      pos.push(x, -y, zc + k * cs + (cs > 0 ? bustDetail(x, y) * smooth(0, 0.35, cs) : 0))
    }
  }
  for (let r = 0; r < rings; r++) {
    for (let s = 0; s < seg; s++) {
      const a = r * seg + s
      const b = r * seg + ((s + 1) % seg)
      const c = (r + 1) * seg + s
      const d = (r + 1) * seg + ((s + 1) % seg)
      idx.push(a, c, b, b, c, d)
    }
  }
  for (const [r, up] of /** @type {[number, boolean][]} */ ([
    [0, true],
    [rings, false],
  ])) {
    const y = BUST_TOP + ((BUST_BOTTOM - BUST_TOP) * r) / rings
    const c = pos.length / 3
    pos.push(0, -y, bustRing(y).zc)
    for (let s = 0; s < seg; s++) {
      const a = r * seg + s
      const b = r * seg + ((s + 1) % seg)
      if (up) idx.push(c, b, a)
      else idx.push(c, a, b)
    }
  }
  const g = new BufferGeometry()
  g.setAttribute('position', new Float32BufferAttribute(pos, 3))
  g.setIndex(idx)
  g.computeVertexNormals()
  return g
}
